import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment, photoEnv } from '../../../../environments/environment';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { formatDate, toDate } from '../../../shared/utils/date.utils';
import { ApiListResponse, VendorMinInf } from '../../../shared/interfaces/common';
import { filterActiveTicketTypes } from '../../../shared/utils/ticket-type.util';
import { Vendors } from '../list/list.service';
import { ViAeS1 } from './steps/interfaces/s1';
import { ViAeS2 } from './steps/interfaces/s2';
import { ViAeS3 } from './steps/interfaces/s3';
import { Media, ViAeS4 } from './steps/interfaces/s4';
import { SavedProduct, ViAeS5 } from './steps/interfaces/s5';
import { IdbCache } from '../../../shared/services/cache.etc/idb.cache';
import {
  VacationPackageDetail,
  VacationPackageItem,
} from '../../../shared/interfaces/vacationPackages';
import { WebsitePreviewPackage } from '../../../shared/components/image-editor/ibranson-preview/ibranson-preview.iface';
import { VendorList } from '../../../shared/interfaces/vendor-details';

export interface VendorItemCategory {
  id: number;
  name: string;
  category: string;
}

export interface VendorItemAmenity {
  id: number;
  amenity?: string;
  searchBar?: string;
  category?: string;
  // UPD 2025-04-16
  listed?: boolean;
  name?: string;
  type?: string;
}

// DEPRECATED!
export interface VendorItemTheatre {
  id: number;
  keepVenueContacts: boolean;
  contacts: {
    phone: string;
    email: string;
    fax: string;
    faxNotify?: boolean;
    emailNotify?: boolean;
  };
  additionalPhone: string;
  address: {
    name: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipCode: string;
    fullAddress: string;
  };
  email: string;
  fax: string;
  phone: string;
  phone2?: string;
  landmark: {
    id: number;
    name: string;
    region: {
      id: number;
      name: string;
    };
  };
  directions: string;
  status: boolean;
  image: string | null;
  category: string;
  locationId: number;
}

// This is New Interface for Theatres
export interface Venue {
  id: number;
  address: {
    name: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zipCode: string;
    fullAddress: string;
  };
  email: string;
  fax: string;
  phone: string;
  landmark: {
    id: number;
    name: string;
    region: {
      id: number;
      name: string;
    };
  };
  directions: string;
  status: boolean;
  category: string;
  // Helpers
  faxNotify?: boolean;
  emailNotify?: boolean;
  keepVenueContacts?: boolean;
  contacts?: {
    email?: string;
    fax?: string;
    phone?: string;
    faxNotify?: boolean;
    emailNotify?: boolean;
  };
}

// используется для добавления продукта из exists products
export interface VendorItemTicketType {
  ticketId: number | string;
  ticketName: string;
  category: number | string;
  rank: number;
  deleted: boolean | null | string;
}

export interface SavedVendorItem {
  id: string;
  owner: string;
  author: string;
  moderator: string;
  status: string;
  version: number;
  vendor: VendorMinInf;
  name: string;
  description: string;
  taxRate: number;
  showInformation: {
    length: number;
    cast: number;
    intermissions: {
      count: number;
      length: number;
    };
  };
  hoursOfOperation: any;
  checkInPolicy: any;
  amenities: {
    id: number;
    amenity: string;
  }[];
  categories: {
    id: number;
    category: string;
  }[];
  venue: any;
  contacts: {
    phone: any;
    fax: any;
    email: any;
  };
  additionalContacts: any;
  directions: any;
  account: {};
  policies: any;
  updated: string;
  created: string;
}

export interface AllMedia {
  coverImage: Media;
  coverVideo?: Media;
  background?: Media;
  seatMap?: Media;
  images: Media[];
  videos?: Media[];
}

@Injectable()
export class AeVendorItemService {
  public currentCategory: string;
  public progress: SavedVendorItem;

  constructor(
    private http: HttpClient,
    private idbc: IdbCache,
  ) {}

  /**
   * Step 1 - get list of categories
   */
  getCategories(): Promise<VendorItemCategory[]> {
    return new Promise<VendorItemCategory[]>((resolve, reject) => {
      this.http
        .get(`${environment.ipiUrl}api/provider/subcategory`) // TODO NEED to UPD to api-v2!
        .subscribe(
          (res: { results: VendorItemCategory[] }) => {
            resolve(
              res.results.filter((i) => {
                return (
                  environment.categoryDec2Str(i.category).api ===
                  environment.categoryDec2Str(this.progress.vendor.category).api
                );
              }),
            );
          },
          (e) => {
            reject(e);
          },
        );
    });
  }

  /**
   * Step 1 - get list of available amenities
   */
  getAmenities(
    category?: 'SHOWS' | 'ATTRACTIONS' | 'DINING' | 'HOTEL' | 'ROOM' | string,
  ): Promise<VendorItemAmenity[]> {
    return new Promise<VendorItemAmenity[]>((resolve, reject) => {
      // this.http.get(`${environment.apiPrefix}/amenity`) // TODO NEED to UPD to api-v2!
      // this.http.get(`${environment.apiPrefix}/amenity/all?type=${category}`)
      this.http
        .get(`${environment.apiV1}/amenity`)
        // this.http.get(`${environment.apiPrefix}/amenity/all`)
        .subscribe(
          (res: { results: VendorItemAmenity[] }) => {
            // resolve(res.results)
            resolve(
              res.results.filter((i) => {
                return (
                  environment.categoryDec2Str(i.category).api ===
                  environment.categoryDec2Str(this.progress.vendor.category).api
                );
              }),
            );
          },
          (e) => {
            reject(e);
          },
        );
    });
  }

  /**
   * Step 2 - get list of theatres
   */
  getTheatres(): Promise<Venue[]> {
    return new Promise<Venue[]>((resolve, reject) => {
      this.http.get(`${environment.apiPrefix}/venue`).subscribe(
        (res: { results: Venue[] }) => {
          resolve(res.results);
        },
        (e) => {
          reject(e);
        },
      );
    });
  }

  /**
   * Step 4 - save photo sorting
   */
  saveSorting(ids: number[]): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.http
        .post(
          `${environment.apiPrefix}/vendor/${environment.categoryDec2Str(this.progress.vendor.category).api}/${this.progress.vendor.vendorId}/gallery/sort`,
          ids,
        )
        .subscribe(
          (res) => {
            resolve(res);
          },
          (error) => {
            reject(error);
          },
        );
    });
  }

  /**
   * Step 4
   * Interpolate the image (RESIZE)
   * @param file: File
   */
  interpolate(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
      const fileAsDataURL = window.URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        photoEnv.resize(img, true).then((res) => {
          resolve(res.toString());
        });
      };
      img.src = fileAsDataURL;
    });
  }

  /**
   * Step 4 - convert File to BASE64
   */
  convertFileToBase64(file: File, filename?: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.interpolate(file).then((res) => {
        // console.log('interpolation data: ', {output: res})
        resolve(res.toString());
      });
    });
  }

  /**
   * Step 5 --- get Ticket Types
   */
  getTicketTypes(): Observable<VendorItemTicketType[]> {
    return new Observable<VendorItemTicketType[]>((observer) => {
      this.http
        .get(`${environment.ipiUrl}api/ticket/name`) // TODO NEED UPD to Api-v2!
        .subscribe(
          (res: { results: VendorItemTicketType[] }) => {
            observer.next(filterActiveTicketTypes(res.results));
          },
          (error) => {
            observer.error(error);
          },
        );
    });
  }

  /**
   * Save Vendor Item step-by-step
   * Firstly - Save Empty item --- {data: {}, step: -1}
   * @param data -
   * @param step -
   */
  saveStepByStep(
    data: ViAeS1 | ViAeS2 | ViAeS3 | ViAeS4 | ViAeS5 | any,
    step: number,
  ): Promise<SavedVendorItem> {
    return new Promise<SavedVendorItem>((resolve, reject) => {
      let TS =
        step === 0
          ? {
              // Step 1 Data (Main Data)
              name: data.name,
              description: data.description,
              categories: data.category.map((i: any) => {
                return { id: i.id, category: i.name };
              }),
              showInformation:
                environment.categoryDec2Str(this.currentCategory).api === 'shows'
                  ? {
                      length: (() => {
                        const H = +formatDate(data.showLength, 'HH');
                        const M = +formatDate(data.showLength, 'mm');
                        return H * 60 + M;
                      })(),
                      cast: +data.castSize,
                      intermissions: {
                        count: data.intermissions,
                        length: data.length,
                      },
                    }
                  : null,
              hoursOfOperation:
                ['attractions', 'dining'].indexOf(
                  environment.categoryDec2Str(this.currentCategory).api,
                ) >= 0
                  ? data.hoursOfOperation
                  : null,
              checkInPolicy:
                ['hotel', 'hotels'].indexOf(
                  environment.categoryDec2Str(this.currentCategory).api,
                ) >= 0
                  ? {
                      checkIn: formatDate(data.checkInPolicy.checkIn, 'hh:mm A').toString(),
                      checkOut: formatDate(data.checkInPolicy.checkOut, 'hh:mm A').toString(),
                    }
                  : null,
              starRating:
                ['hotel', 'hotels'].indexOf(
                  environment.categoryDec2Str(this.currentCategory).api,
                ) >= 0
                  ? data.starRating
                  : null,
              amenities: data.amenities.map((i: any) => {
                return { id: i.id, amenity: i.amenity };
              }),
              taxRate: +data.salesTax,
            }
          : step === -1
            ? {
                owner: data.owner,
              }
            : this.progress;
      const step2 =
        step === 1
          ? {
              keepVenueContacts: data.venue.keepVenueContacts,
              additionalContacts: data.additionalContacts.map((i: any) => {
                return i.type === 'phone'
                  ? {
                      phone: i.data,
                    }
                  : i.type === 'fax'
                    ? {
                        fax: i.data,
                        notify: i.send,
                      }
                    : {
                        email: i.data,
                        notify: i.send,
                      };
              }),
              venue: data.venue.id,
              contacts: {
                phone: data.venue.phone,
                fax: {
                  fax: data.venue.fax,
                  notify: data.venue.contacts.faxNotify ? true : false,
                },
                email: {
                  email: data.venue.email,
                  notify: data.venue.contacts.emailNotify ? true : false,
                },
              },
              directions: data.venue.directions,
              account: {
                name: data.name,
                address1: data.address,
                address2: null,
                zipCode: data.zipCode,
                city: data.city,
                state: data.state,
              },
            }
          : {};
      const step3 =
        step === 2
          ? {
              // policies: null
              policies: {
                voucher: {
                  exchange: data.voucherExchangePolicy,
                  paperless: data.cb1,
                },
                cancellation: {
                  description: data.cancellationPolicy,
                  nonRefundable: data.nonRefundable,
                  hours: data.period,
                },
                reservation: {
                  cutOff: data.reservationCutOffPolicy,
                },
                satisfaction: data.cb2,
              },
            }
          : {};
      const step4 = {}; // Media - saved separately to other end-point
      const step5 = {};
      if (step === 1) {
        TS = { ...TS, ...step2 };
      } else if (step === 2) {
        TS = { ...TS, ...step2, ...step3 };
      } else if (step === 3) {
        TS = { ...TS, ...step2, ...step3, ...step4 };
      } else if (step === 4) {
        TS = { ...TS, ...step2, ...step3, ...step4, ...step5 };
      }
      console.log('show OBJ before send: ', TS);
      if (!this.progress) {
        this.http
          .post(
            `${environment.apiPrefix}/vendor/${environment.categoryDec2Str(this.currentCategory).api}`,
            TS,
          )
          .subscribe(
            (res: SavedVendorItem) => {
              // Update data in Cache
              this.idbc.forceCacheUpdate();

              this.progress = res;
              resolve(res);
            },
            (error) => {
              reject(error);
            },
          );
      } else {
        this.http
          .put(
            `${environment.apiPrefix}/vendor/${environment.categoryDec2Str(this.progress.vendor.category).api}/${this.progress.vendor.vendorId}/form`,
            TS,
          )
          .subscribe(
            (res: SavedVendorItem) => {
              // Update data in Cache
              this.idbc.forceCacheUpdate();

              this.progress = res;
              resolve(res);
            },
            (error) => {
              reject(error);
            },
          );
      }
    });
  }

  /**
   * MODERATION
   * @param formData --- steps array
   * @param modData --- moderation array
   */
  moderation(formData: any, modData: any): Promise<any> {
    console.log('MODERATION: ', formData, modData);
    const $applied = {
      // Step 1
      vendor: this.progress.vendor,
      name: formData[0].data.name,
      description: formData[0].data.description,
      categories: formData[0].data.category.map((i: any) => {
        return { id: i.id, category: i.name };
      }),
      starRating:
        ['hotel', 'hotels'].indexOf(
          environment.categoryDec2Str(this.progress.vendor.category).api,
        ) >= 0
          ? formData[0].data.starRating
          : null,
      showInformation:
        environment.categoryDec2Str(this.currentCategory).api === 'shows'
          ? {
              length: (() => {
                const H = +formatDate(formData[0].data.showLength, 'HH');
                const M = +formatDate(formData[0].data.showLength, 'mm');
                return H * 60 + M;
              })(),
              cast: +formData[0].data.castSize,
              intermissions: {
                count: formData[0].data.intermissions,
                length: formData[0].data.length,
              },
            }
          : null,
      hoursOfOperation:
        ['attractions', 'dining'].indexOf(environment.categoryDec2Str(this.currentCategory).api) >=
        0
          ? formData[0].data.hoursOfOperation
          : null,
      checkInPolicy:
        ['hotel', 'hotels'].indexOf(environment.categoryDec2Str(this.currentCategory).api) >= 0
          ? {
              checkIn: formatDate(formData[0].data.checkInPolicy.checkIn, 'hh:mm A').toString(),
              checkOut: formatDate(formData[0].data.checkInPolicy.checkOut, 'hh:mm A').toString(),
            }
          : null,
      amenities: formData[0].data.amenities.map((i: any) => {
        return { id: i.id, amenity: i.amenity };
      }),
      taxRate: +formData[0].data.salesTax,

      // Step 2
      additionalContacts: formData[1].data.additionalContacts.map((i: any) => {
        return i.type === 'phone'
          ? {
              phone: i.data,
            }
          : i.type === 'fax'
            ? {
                fax: i.data,
                notify: i.send,
              }
            : {
                email: i.data,
                notify: i.send,
              };
      }),
      venue: formData[1].data.venue.id,
      contacts: {
        phone: formData[1].data.venue.phone,
        fax: {
          fax: formData[1].data.venue.fax,
          notify: formData[1].data.venue.contacts.faxNotify ? true : false,
        },
        email: {
          email: formData[1].data.venue.email,
          notify: formData[1].data.venue.contacts.emailNotify ? true : false,
        },
      },
      directions: formData[1].data.venue.directions,
      keepVenueContacts: formData[1].data.venue.keepVenueContacts,
      account: {
        name: formData[1].data.name,
        address1: formData[1].data.address,
        address2: null as any,
        zipCode: formData[1].data.zipCode,
        city: formData[1].data.city,
        state: formData[1].data.state,
      },

      // Step 3
      policies: {
        voucher: {
          exchange: formData[2].data.voucherExchangePolicy,
          paperless: formData[2].data.cb1,
        },
        cancellation: {
          description: formData[2].data.cancellationPolicy,
          nonRefundable: formData[2].data.nonRefundable,
          hours: formData[2].data.period,
        },
        reservation: {
          cutOff: formData[2].data.reservationCutOffPolicy,
        },
        satisfaction: formData[2].data.cb2,
      },
    };
    const $rejected = (() => {
      const R = [];
      const instances = [
        [
          'name',
          'description',
          'categories',
          'amenities',
          'showInformation',
          'hoursOfOperation',
          'checkInPolicy',
          'taxRate',
        ],
        ['venue', 'phone', 'fax', 'email', 'account', 'additionalContacts'],
        ['voucher', 'cancellation', 'reservation'],
      ];
      /**
       * directions, --- пока не чипати
       * hoursOfOperation --- for attractions and dining
       * checkInPolicy --- for hotels
       */

      for (const step of [0, 1, 2]) {
        for (const instance of instances[step]) {
          if (modData[step][instance].rejected) {
            R.push({
              propertyName: instance,
              reason: modData[step][instance].reason,
            });
          }
        }
      }
      return R;
    })();
    const TS = {
      applied: $applied,
      rejected: $rejected,
    };
    return new Promise<any>((resolve, reject) => {
      if ($rejected.length > 0) {
        this.http
          .post(
            `${environment.apiPrefix}/vendor/${environment.categoryDec2Str(this.progress.vendor.category).api}/${this.progress.vendor.vendorId}/reject`,
            TS,
          )
          .subscribe(
            (res) => {
              // Update data in Cache
              this.idbc.forceCacheUpdate();

              resolve(res);
            },
            (error) => {
              reject(error);
            },
          );
      } else {
        this.http
          .post(
            `${environment.apiPrefix}/vendor/${environment.categoryDec2Str(this.progress.vendor.category).api}/${this.progress.vendor.vendorId}/approve`,
            TS,
          )
          .subscribe(
            (res) => {
              // Update data in Cache
              this.idbc.forceCacheUpdate();

              resolve(res);
            },
            (error) => {
              reject(error);
            },
          );
      }
    });
  }

  registerVendorProduct(data: any): Promise<SavedProduct> {
    const TS = {
      name: data.type.ticketName,
      description: data.description,
      fullDescription: data.fullDescription,
      amenities: data.amenities
        ? typeof data.amenities[0] === 'string'
          ? data.amenities
          : data.amenities.map((i: any) => i.name)
        : [],
      notes: data.notes,
      rate: {
        fit: {
          amount: +data.fitRate,
          taxInclusive: data.frTaxInclusive,
        },
        boxOfficeRate: {
          amount: +data.boxOffice,
          taxInclusive: data.boTaxInclusive,
        },
      },
      group: {
        min: data.groupRestrictionMin,
        max: data.groupRestrictionMax,
      },
      age: {
        min: data.ageRestrictionMin,
        max: data.ageRestrictionMax,
      },
      stopSellInDayOfEvent: data.stopSellInDayOfEvent,
      sellOnlyInDayOfEvent: data.sellOnlyInDayOfEvent,
    };
    return new Promise<SavedProduct>((resolve, reject) => {
      this.http
        .post(
          `${environment.apiPrefix}/vendor/${environment.categoryDec2Str(this.progress.vendor.category).api}/${this.progress.vendor.vendorId}/product`,
          TS,
        )
        .subscribe(
          (res: SavedProduct) => {
            // Update data in Cache
            this.idbc.forceCacheUpdate();

            resolve(res);
          },
          (error) => {
            reject(error);
          },
        );
    });
  }

  getVendorProducts(category: string, id: string): Promise<SavedProduct[]> {
    const catRaw = environment.categoryDec2Str(category);
    // костиль для запиту в АПІ для готелів
    category = [1, 5].indexOf(catRaw.id) >= 0 ? 'hotel' : catRaw.api;
    return new Promise((resolve, reject) => {
      this.http
        .get(
          `${environment.apiPrefix}/vendor/${environment.categoryDec2Str(category).api}/${id}/product `,
        )
        .subscribe(
          (res: { results: SavedProduct[] }) => {
            resolve(res.results);
          },
          (e) => {
            reject(e);
          },
        );
    });
  }

  removeVendorProduct(id: number | string): Promise<any> {
    const C = this.progress.vendor.category;
    const I = this.progress.vendor.vendorId;
    return new Promise<boolean>((resolve, reject) => {
      this.http
        .delete(
          `${environment.apiPrefix}/vendor/${environment.categoryDec2Str(C).api}/${I}/product/${id}`,
        )
        .subscribe(
          (res) => {
            // Update data in Cache
            this.idbc.forceCacheUpdate();

            resolve(true);
          },
          (error) => {
            reject();
          },
        );
    });
  }

  updateVendorProduct(id: number | string, data: any): Promise<SavedProduct> {
    const C = this.progress.vendor.category;
    const I = this.progress.vendor.vendorId;
    const TS = {
      name: data.type.ticketName,
      description: data.description,
      fullDescription: data.fullDescription,
      amenities: data.amenities
        ? typeof data.amenities[0] === 'string'
          ? data.amenities
          : data.amenities.map((i: any) => i.name)
        : [],
      notes: data.notes,
      // UPD 4 may
      rate: {
        fit: {
          amount: data.fitRate,
          taxInclusive: data.frTaxInclusive,
        },
        boxOfficeRate: {
          amount: data.boxOffice,
          taxInclusive: data.boTaxInclusive,
        },
      },
      group: {
        min: data.groupRestrictionMin,
        max: data.groupRestrictionMax,
      },
      age: {
        min: data.ageRestrictionMin,
        max: data.ageRestrictionMax,
      },
      stopSellInDayOfEvent: data.stopSellInDayOfEvent,
      sellOnlyInDayOfEvent: data.sellOnlyInDayOfEvent,
    };
    return new Promise<SavedProduct>((resolve, reject) => {
      this.http
        .put(
          `${environment.apiPrefix}/vendor/${environment.categoryDec2Str(C).api}/${I}/product/${id}`,
          TS,
        )
        .subscribe(
          (res: SavedProduct) => {
            // Update data in Cache
            this.idbc.forceCacheUpdate();

            resolve(res);
          },
          (error) => {
            reject();
          },
        );
    });
  }

  uploadMedia(
    $type: 'VIDEO' | 'IMAGE' | string,
    $content: string,
    $tags?: string[],
  ): Promise<Media> {
    const TS = {
      type: $type,
      content: $content,
      category: this.progress.vendor.category,
      tags: $tags,
    };
    return new Promise<Media>((resolve, reject) => {
      this.http
        .post(
          `${environment.apiPrefix}/vendor/${environment.categoryDec2Str(this.progress.vendor.category).api}/${this.progress.vendor.vendorId}/gallery`,
          TS,
        )
        .subscribe(
          (res: Media) => {
            resolve(res);
          },
          (error) => {
            reject(error);
          },
        );
    });
  }

  removeMedia(contentId?: number): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .delete(
          `${environment.apiPrefix}/vendor/${environment.categoryDec2Str(this.progress.vendor.category).api}/${this.progress.vendor.vendorId}/gallery/${contentId}`,
        )
        .subscribe(
          () => {
            resolve(true);
          },
          (error) => {
            reject();
          },
        );
    });
  }

  getAllMedia(category: string, id: string): Promise<AllMedia> {
    return new Promise<AllMedia>((resolve, reject) => {
      // const C = category ? environment.categoryDec2Str(category).api :
      //   environment.categoryDec2Str(this.progress.vendor.category).api
      const catRaw = environment.categoryDec2Str(category);
      // костиль для запиту в АПІ для готелів
      category = [1, 5].indexOf(catRaw.id) >= 0 ? 'hotel' : catRaw.api;
      const I = id ? id : this.progress.vendor.vendorId;
      this.http.get(`${environment.apiPrefix}/vendor/${category}/${I}/gallery/applied`).subscribe(
        (res: AllMedia) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  getVendorItemDetails(category: string, id: string): Promise<Vendors> {
    return new Promise<Vendors>((resolve, reject) => {
      const catRaw = environment.categoryDec2Str(category);
      // костиль для запиту в АПІ для готелів
      category = [1, 5].indexOf(catRaw.id) >= 0 ? 'hotel' : catRaw.api;
      this.http.get(`${environment.apiPrefix}/vendor/${category}/${id}/form`).subscribe(
        (res: Vendors) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  /**
   * Packages that include this vendor — for iBranson website preview.
   */
  getPreviewPackages(
    vendor: VendorMinInf,
    opts?: { currentCover?: string | null; currentName?: string },
  ): Promise<WebsitePreviewPackage[]> {
    const cat = environment.categoryDec2Str(vendor.category);
    const categoryPath = cat.api || String(vendor.category);
    const vendorId = String(vendor.vendorId);

    return new Promise((resolve) => {
      this.http
        .get<ApiListResponse<VacationPackageItem>>(
          `${environment.apiPrefix}/vp/${categoryPath}/${vendorId}`,
        )
        .pipe(
          map((res) => (res?.results || []).filter((pkg) => pkg?.status !== false)),
          switchMap((list) => {
            if (!list.length) {
              return of([] as WebsitePreviewPackage[]);
            }
            const details$ = list.map((pkg) =>
              this.http.get<VacationPackageDetail>(`${environment.apiPrefix}/vp/${pkg.id}`).pipe(
                catchError(() => of(pkg as unknown as VacationPackageDetail)),
              ),
            );
            return forkJoin(details$).pipe(
              switchMap((details) =>
                this.resolvePreviewPackageMedia(details, vendor, opts).pipe(
                  map((covers) =>
                    details.map((detail) => this.mapPreviewPackage(detail, vendor, covers, opts)),
                  ),
                ),
              ),
            );
          }),
          catchError(() => of([] as WebsitePreviewPackage[])),
        )
        .subscribe((packages) => resolve(packages));
    });
  }

  private resolvePreviewPackageMedia(
    details: VacationPackageDetail[],
    currentVendor: VendorMinInf,
    opts?: { currentCover?: string | null; currentName?: string },
  ): Observable<Record<string, string | null>> {
    const vendors = new Map<string, VendorMinInf>();
    for (const detail of details) {
      for (const product of detail.products || []) {
        const v = product?.vendor;
        if (!v?.vendorId) {
          continue;
        }
        vendors.set(`${v.category}:${v.vendorId}`, {
          category: String(v.category),
          vendorId: v.vendorId,
        });
      }
    }

    if (!vendors.size) {
      return of({});
    }

    const categories = Array.from(
      new Set(
        Array.from(vendors.values()).map((v) => environment.categoryDec2Str(v.category).api),
      ),
    );
    const namesByCategory$ = forkJoin(
      categories.map((api) =>
        this.http.get<Record<string, VendorList> | VendorList[]>(`${environment.apiPrefix}/vendor/names/${api}`).pipe(
          map((res) => {
            const list = Array.isArray(res) ? res : Object.values(res || {});
            const mapNames: Record<string, string> = {};
            for (const item of list) {
              const id = String(item.vendorId ?? item.vendor?.vendorId ?? '');
              if (id) {
                mapNames[id] = item.name || '';
              }
            }
            return { api, mapNames };
          }),
          catchError(() => of({ api, mapNames: {} as Record<string, string> })),
        ),
      ),
    ).pipe(
      map((rows) => {
        const byApi: Record<string, Record<string, string>> = {};
        for (const row of rows) {
          byApi[row.api] = row.mapNames;
        }
        return byApi;
      }),
    );

    const coverRequests = Array.from(vendors.values()).map((v) => {
      const key = `${v.category}:${v.vendorId}`;
      const same =
        String(v.vendorId) === String(currentVendor.vendorId) &&
        environment.categoryDec2Str(v.category).id ===
          environment.categoryDec2Str(currentVendor.category).id;
      if (same && opts?.currentCover) {
        return of({ key, cover: opts.currentCover as string | null });
      }
      return new Observable<{ key: string; cover: string | null }>((subscriber) => {
        this.getAllMedia(String(v.category), String(v.vendorId))
          .then((media) => {
            subscriber.next({ key, cover: media?.coverImage?.contentId || null });
            subscriber.complete();
          })
          .catch(() => {
            subscriber.next({ key, cover: null });
            subscriber.complete();
          });
      });
    });

    return forkJoin({
      namesByCategory: namesByCategory$,
      covers: forkJoin(coverRequests).pipe(catchError(() => of([]))),
    }).pipe(
      map(({ namesByCategory, covers }) => {
        const out: Record<string, string | null> = {};
        for (const v of vendors.values()) {
          const key = `${v.category}:${v.vendorId}`;
          const api = environment.categoryDec2Str(v.category).api;
          out[`name:${key}`] = namesByCategory[api]?.[String(v.vendorId)] || null;
        }
        for (const row of covers) {
          out[`cover:${row.key}`] = row.cover;
        }
        return out;
      }),
      catchError(() => of({})),
    );
  }

  private mapPreviewPackage(
    detail: VacationPackageDetail,
    currentVendor: VendorMinInf,
    media: Record<string, string | null>,
    opts?: { currentCover?: string | null; currentName?: string },
  ): WebsitePreviewPackage {
    const period = (detail.periods || [])[0];
    const items = (detail.products || [])
      .map((product) => {
        const v = product?.vendor;
        if (!v?.vendorId) {
          return null;
        }
        const key = `${v.category}:${v.vendorId}`;
        const same =
          String(v.vendorId) === String(currentVendor.vendorId) &&
          environment.categoryDec2Str(v.category).id ===
            environment.categoryDec2Str(currentVendor.category).id;
        const name =
          (same && opts?.currentName) ||
          v.details?.name ||
          v.vendorName ||
          media[`name:${key}`] ||
          `Item ${v.vendorId}`;
        return {
          name,
          image: media[`cover:${key}`] || (same ? opts?.currentCover || null : null),
        };
      })
      .filter(Boolean) as WebsitePreviewPackage['items'];

    return {
      name: detail.name,
      saveUpTo: detail.saveUpTo != null ? Number(detail.saveUpTo) : null,
      startDate: this.formatPreviewDate(period?.startDate),
      endDate: this.formatPreviewDate(period?.endDate),
      descriptionHtml: detail.description || '',
      items,
    };
  }

  private formatPreviewDate(value?: string | null): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
}
